import { useEffect } from "react";
import { Route, createBrowserRouter, createRoutesFromElements } from "react-router-dom";
import { useLocation, useNavigate, useParams } from "react-router-dom";



import ErrorPage from "@/pages/404.tsx";
import ServerErrorPage from "@/pages/500.tsx";
import CatalogPage from "@/pages/CatalogPage.tsx";
import ContactsPage from "@/pages/ContactsPage.tsx";
import FavoritesPage from "@/pages/FavoritesPage.tsx";
import HistoryPage from "@/pages/HistoryPage.tsx";
import LicensePage from "@/pages/LicensePage.tsx";
import MainPage from "@/pages/MainPage.tsx";
import PremierePage from "@/pages/PremierePage.tsx";
import PrivacyPage from "@/pages/PrivacyPage.tsx";
import SubscriptionsPage from "@/pages/SubscriptionsPage.tsx";
import TvChannelsPage from "@/pages/TvChannelsPage.tsx";
import CollectionsPage from "@/pages/collections/CollectionsPage.tsx";
import CollectionsSinglePage from "@/pages/collections/CollectionsSinglePage.tsx";
import CountrySinglePage from "@/pages/country/CountrySinglePage.tsx";
import DetailActorsPage from "@/pages/detail/DetailActorsPage.tsx";
import DetailCommentsPage from "@/pages/detail/DetailCommentsPage.tsx";
import DetailEpisodesPage from "@/pages/detail/DetailEpisodesPage.tsx";
import DetailPage from "@/pages/detail/DetailPage.tsx";
import DetailRatePage from "@/pages/detail/DetailRatePage.tsx";
import DistributorGenrePage from "@/pages/distributor/DistributorGenrePage.tsx";
import DistributorPage from "@/pages/distributor/DistributorPage.tsx";
import ProfilesPage from "@/pages/gate/ProfilesPage.tsx";
import UpdateProfilePage from "@/pages/gate/UpdateProfilePage.tsx";
import GenresPage from "@/pages/genres/GenresPage.tsx";
import GenresSinglePage from "@/pages/genres/GenresSinglePage.tsx";
import NotificationIdPage from "@/pages/notifications/NotificationIdPage.tsx";
import NotificationsPage from "@/pages/notifications/NotificationsPage.tsx";
import PersonDetailsPage from "@/pages/persons/PersonDetailsPage.tsx";
import PersonsPage from "@/pages/persons/PersonsPage.tsx";
import PersonsSinglePage from "@/pages/persons/PersonsSinglePage.tsx";
import SearchContentPage from "@/pages/search/SearchContentPage.tsx";
import SearchPage from "@/pages/search/SearchPage.tsx";
import StaffNavigationsPage from "@/pages/staff/StaffNavigationsPage.tsx";
import StaffOrderMoviePage from "@/pages/staff/StaffOrderMoviePage.tsx";
import StaffPaymentPage from "@/pages/staff/StaffPaymentPage.tsx";
import StaffSessionsPage from "@/pages/staff/StaffSessionsPage.tsx";
import StaffSubscriptionPage from "@/pages/staff/StaffSubscriptionPage.tsx";
import StaffUserSettingsPage from "@/pages/staff/StaffUserSettingsPage.tsx";



import NestedRoutes from "@/components/helpers/NestedRoutes.tsx";
import GateLayout from "@/components/layout/GateLayout/GateLayout.tsx";
import MainLayout from "@/components/layout/MainLayout.tsx";
import StaffLayout from "@/components/layout/StaffLayout/StaffLayout.tsx";



import App from "@/App.tsx";
import { ROUTES } from "@/config/routes.ts";
import { broadcastCategoriesLoader, catalogFilterLoader, genreDetailLoader, genresLoader, personDetailLoader, refreshAccessLoader } from "@/loaders";
import { collectionDetailLoader } from "@/loaders/collectionDetail.loader.ts";
import { removeProfileInfoLoader } from "@/loaders/removeProfileInfoLoader.ts";
import { updateProfileLoader } from "@/loaders/updateProfile.loader.ts";



import { countryDetailLoader } from "./loaders/countryDetail.loader";
import { yearDetailLoader } from "./loaders/yearDetail.loader";
import EpisodePage from "./pages/detail/episode/EpisodePage";
import SerialEpisode from "./pages/detail/serial-episode/SerialEpisode";
import LivePlayer from "./pages/livePlayer/LivePlayer";
import YearSinglePage from "./pages/year/YearSinglePage";


const SUPPORTED_LOCALES = ["uz", "ru"];

const LocaleRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { locale } = useParams();

  useEffect(() => {
    if (SUPPORTED_LOCALES.includes(locale!)) {
      const newPath = location.pathname.replace(`/${locale}`, "") || "/";
      navigate(newPath + location.search + location.hash, { replace: true });
    } else {
      navigate("/" + location.search + location.hash, { replace: true });
    }
  }, [locale, location, navigate]);

  return null;
};

export default LocaleRedirect;

export const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path=":locale/*" element={<LocaleRedirect />} />

      <Route path="/" element={<App />}>
        <Route path={ROUTES.DETAIL.EPISODE} element={<EpisodePage />} />
        <Route path={ROUTES.DETAIL.SERIAL_EPISODE} element={<SerialEpisode />} />
        <Route path={ROUTES.CHANNELS.SINGLE} element={<LivePlayer />} />
        <Route element={<MainLayout />}>
          <Route loader={refreshAccessLoader} shouldRevalidate={() => false}>
            <Route path={ROUTES.MAIN.MAIN} element={<MainPage />} />

            <Route path={ROUTES.DETAIL.MAIN} element={<NestedRoutes />}>
              <Route index element={<DetailPage />} />
              <Route path={ROUTES.DETAIL.ACTORS} element={<DetailActorsPage />} />
              <Route path={ROUTES.DETAIL.COMMENTS} element={<DetailCommentsPage />} />
              <Route path={ROUTES.DETAIL.RATE} element={<DetailRatePage />} />
              <Route path={ROUTES.DETAIL.EPISODES} element={<DetailEpisodesPage />} />
            </Route>
            <Route path={ROUTES.MAIN.CATALOG} element={<CatalogPage />} loader={catalogFilterLoader} />
            <Route path={ROUTES.MAIN.COLLECTIONS} element={<CollectionsPage />} />
            <Route path={ROUTES.MAIN.PREMIERE} element={<PremierePage />} />
            <Route path={ROUTES.MAIN.COLLECTION} element={<CollectionsSinglePage />} loader={collectionDetailLoader} />
            <Route path={ROUTES.MAIN.DISTRIBUTOR} element={<NestedRoutes />}>
              <Route index element={<DistributorPage />} />
              <Route path={ROUTES.MAIN.DISTRIBUTOR_GENRE} element={<DistributorGenrePage />} />
            </Route>
            <Route path={ROUTES.MAIN.GENRES} element={<GenresPage />} loader={genresLoader} />
            <Route path={ROUTES.MAIN.GENRE} element={<GenresSinglePage />} loader={genreDetailLoader} />
            <Route path={ROUTES.MAIN.COUNTRY} element={<CountrySinglePage />} loader={countryDetailLoader} />
            <Route path={ROUTES.MAIN.YEAR} element={<YearSinglePage />} loader={yearDetailLoader} />
            <Route path={ROUTES.PERSONS.MAIN} element={<PersonsPage />} />
            <Route path={ROUTES.PERSONS.SINGLE} element={<PersonsSinglePage />} />
            <Route path={ROUTES.PERSON_DETAILS} element={<PersonDetailsPage />} loader={personDetailLoader} />
            <Route path={ROUTES.SEARCH} element={<SearchPage />} />
            <Route path={ROUTES.SEARCH_CONTENT} element={<SearchContentPage />} />
            <Route path={ROUTES.NOTIFICATIONS.MAIN} element={<NestedRoutes />}>
              <Route index element={<NotificationsPage />} />
              <Route path={ROUTES.NOTIFICATIONS.SINGLE} element={<NotificationIdPage />} />
            </Route>
            <Route path={ROUTES.FAVORITES} element={<FavoritesPage />} />
            <Route path={ROUTES.HISTORY} element={<HistoryPage />} />
            <Route element={<StaffLayout />}>
              <Route path={ROUTES.STAFF.NAVIGATIONS} element={<StaffNavigationsPage />} />
              <Route path={ROUTES.STAFF.USER_SETTINGS} element={<StaffUserSettingsPage />} />
              <Route path={ROUTES.STAFF.PAYMENT_HISTORY} element={<StaffPaymentPage />} />
              <Route path={ROUTES.STAFF.SESSIONS} element={<StaffSessionsPage />} />
              <Route path={ROUTES.STAFF.AVAILABLE_SUBSCRIPTIONS} element={<StaffSubscriptionPage />} />
              <Route path={ROUTES.STAFF.ORDER_MOVIE} element={<StaffOrderMoviePage />} />
            </Route>
            <Route path={ROUTES.SUBS} element={<SubscriptionsPage />} />
            <Route path={ROUTES.CHANNELS.MAIN} element={<TvChannelsPage />} loader={broadcastCategoriesLoader} />
            <Route path={ROUTES.CONTACTS} element={<ContactsPage />} />
          </Route>
          <Route element={<GateLayout />} shouldRevalidate={() => false}>
            <Route loader={removeProfileInfoLoader}>
              <Route path={ROUTES.GATE.PROFILES} loader={removeProfileInfoLoader} element={<ProfilesPage />} />
              <Route path={ROUTES.GATE.UPDATE_PROFILE} element={<UpdateProfilePage />} loader={updateProfileLoader} />
            </Route>
          </Route>

          <Route path={ROUTES.PRIVACY} element={<PrivacyPage />} />
          <Route path={ROUTES.LICENSE} element={<LicensePage />} />
        </Route>
        <Route path={ROUTES.SERVER_ERROR} element={<ServerErrorPage />} />
        <Route path={"*"} element={<ErrorPage />} />
      </Route>
    </>,
  ),
);